/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CollectionsModule, MODULE_LAYOUTS } from '@coursera-web/cds-labs';

import { Button, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import PrePurchaseUpsellCourseraPlusModal from 'bundles/coursera-plus/components/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusModal';
import { useXdpRecommendations } from 'bundles/unified-description-page-common/providers/specialization/RecommendationsDataProvider';

import CourseraPlusLogo from 'images/coursera-plus/coursera-plus-blue.png';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/xdp';

const CourseraPlusRecommendationsModule = () => {
  const xdpRecommendationsData = useXdpRecommendations();
  const track = useTracker();
  const visibilityTrackingRef = useVisibilityTracker('view_page_section', {
    pageSection: { sectionName: 'upsell' },
  });
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  if (!xdpRecommendationsData?.recommendationCollectionEntities?.length) return null;

  const { recommendationCollectionEntities, collectionTrackingId, eventingRecommender, loading } =
    xdpRecommendationsData;
  const title = _t('Get access to all these courses and more with a subscription');

  const carouselCollection: CarouselCollection = {
    title,
    collectionId: collectionTrackingId,
    entities: recommendationCollectionEntities,
    collectionTrackingData: {
      carouselRecommender: eventingRecommender,
    },
    // in the elevate LTV experiment there is usually a logged in and logged out cta, but
    // because the user cannot see this carousel when logged in, we don't need to worry about the two separate cases
    renderCustomPrimaryCta: ({ size, fullWidth, variantOverride }) => (
      <Button
        size={size}
        fullWidth={fullWidth}
        variant={variantOverride ?? 'primary'}
        onClick={() => {
          setIsModalOpen(true);
          track('click_button', {
            button: {
              name: 'enroll',
            },
          });
        }}
        css={
          fullWidth
            ? undefined
            : css`
                ${breakpoints.down('xs')} {
                  margin-right: var(--cds-spacing-200);
                }
              `
        }
      >
        {_t('Enroll for Free')}
      </Button>
    ),
  };

  return (
    <React.Fragment>
      <PrePurchaseUpsellCourseraPlusModal handleClose={() => setIsModalOpen(false)} isOpen={isModalOpen} />
      <div ref={visibilityTrackingRef as React.MutableRefObject<HTMLDivElement>}>
        <CollectionsModule
          headline={title}
          isCollectionsLoading={loading}
          collections={[carouselCollection]}
          layout={MODULE_LAYOUTS.textTopImageBottom}
          eyebrow={{
            image: {
              url: CourseraPlusLogo,
              height: 17.5,
              description: _t('Coursera Plus'),
            },
          }}
        />
      </div>
    </React.Fragment>
  );
};

export default CourseraPlusRecommendationsModule;
